import React, { useState, useEffect, useRef } from 'react';
import { MsFormulaire } from '../types';
import { adresseUneLigne } from '../../../utils/adresse';
import {
  computeFinConsultation,
  computeDateDebutOperation,
  computeDateFinOperation,
  isValidEmail,
  isValidPhoneFr,
  FORMAT_ERROR_MS,
  CHAMP_PHRASE_MAX,
} from '../domain/operationConsultation';

// Sous-bloc "Operation" du Descriptif administratif (refonte xlsx). Champs editables, grille 12
// colonnes fidele au xlsx. La duree de consultation (Descriptif procedural) et la visite
// (Particularites) ne sont PAS ici. Lit/ecrit le sac formulaire (MsFormulaire) ; le gating viewMode
// est decide par le parent. Conserve l'autocomplete adresse IGN et le calcul des dates.

interface IgnSuggestion {
  label: string;
  postcode?: string;
  city?: string;
}

// Forme (partielle) d'un resultat de l'API de geocodage geopf.fr (reponse externe).
interface IgnApiResult {
  fulltext?: string;
  zipcode?: string;
  city?: string;
}

interface OperationFormulaireAdminProps {
  formData: MsFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: MsFormulaire) => void;
}

const OperationFormulaireAdmin: React.FC<OperationFormulaireAdminProps> = ({
  formData,
  viewMode,
  errors = {},
  setFormData,
}) => {
  // Ce qui est AFFICHE dans le champ adresse : la ligne complete (voie, code postal ville, pays).
  // Ce qui est PERSISTE reste `formData.adresse` = la voie seule, les trois autres gardant leur
  // colonne. Sans cette dissociation, choisir une suggestion ecrirait la ville et le pays dans la
  // colonne adresse, et on les retrouverait en double a la lecture.
  const adresseComplete = adresseUneLigne(formData.adresse, formData.codePostal, formData.ville, formData.pays);
  const [adresseInput, setAdresseInput] = useState(adresseComplete);
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  // Champs touches (blur) pour le feedback live de format mail / telephone.
  const [touched, setTouched] = useState<{ moeMail?: boolean; moeTelephone?: boolean }>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false);
  const hasUserTypedRef = useRef(false);
  const hasManuallyEditedDateRef = useRef(false);

  const update = (patch: Partial<MsFormulaire>) => {
    if (setFormData) setFormData({ ...formData, ...patch });
  };

  // Suggestions IGN (apres saisie, debounce 300ms, >= 3 car.).
  useEffect(() => {
    if (!hasUserTypedRef.current) {
      setAdresseSuggestions([]);
      return;
    }
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(
            adresseInput,
          )}&index=StreetAddress&limit=5`,
        );
        if (response.ok) {
          const json = (await response.json()) as { results?: IgnApiResult[] };
          const suggestions: IgnSuggestion[] = (json.results || []).map((item) => ({
            label: item.fulltext || '',
            postcode: item.zipcode || '',
            city: item.city || '',
          }));
          setAdresseSuggestions(suggestions);
        }
      } catch {
        // Autocomplete non critique : on degrade silencieusement.
        setAdresseSuggestions([]);
      }
    }, 300);
  }, [adresseInput]);

  // Resynchronise le champ quand l'operation est (re)hydratee depuis l'API. On compare la ligne
  // COMPLETE : comparer la seule voie remettrait le champ a la voie juste apres le choix d'une
  // suggestion, effacant a l'ecran le code postal et la ville qu'on vient d'y mettre.
  useEffect(() => {
    if (adresseComplete !== adresseInput) setAdresseInput(adresseComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adresseComplete]);

  const handleAdresseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserTypedRef.current = true;
    setAdresseInput(e.target.value);
    // Taper librement INVALIDE la derivation : code postal, ville et pays ne sont plus affiches,
    // ils ne peuvent donc etre renseignes que par une suggestion IGN. Les vider ici est ce qui rend
    // la regle verifiable — sinon une adresse tapee a la main conserverait le code postal d'une
    // adresse precedente, et partirait en base avec une localisation fausse.
    update({ adresse: e.target.value, codePostal: '', ville: '', pays: '' });
  };

  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    isSelectingRef.current = true;
    hasUserTypedRef.current = false;
    const adresse = suggestion.label.includes(',')
      ? suggestion.label.split(',')[0].trim()
      : suggestion.label;
    // L'API IGN (geopf.fr) ne renvoie que des adresses francaises -> pays = France.
    const codePostal = suggestion.postcode || '';
    const ville = suggestion.city || '';
    update({ adresse, codePostal, ville, pays: 'France' });
    // Le champ affiche l'adresse ENTIERE une fois la suggestion retenue : c'est le seul retour visuel
    // qui confirme quelle commune a ete geocodee. La voie seule laisserait croire que le choix n'a
    // pas ete pris en compte, deux rues homonymes etant alors indistinguables a l'ecran.
    setAdresseInput(adresseUneLigne(adresse, codePostal, ville, 'France'));
    setAdresseSuggestions([]);
  };

  // Fin de consultation (borne la date de debut d'operation) — la duree de consultation vit dans
  // le Descriptif procedural ; ici on utilise la valeur du formulaire (ou son defaut).
  const finConsultationStr = computeFinConsultation(
    formData.dateDebutConsultation,
    Number(formData.delaisConsultation || 10),
  );

  // Recalcule la date de debut d'operation depuis la duree de consultation (sauf edition manuelle).
  useEffect(() => {
    if (!setFormData || viewMode || hasManuallyEditedDateRef.current) return;
    const debutStr = computeDateDebutOperation(Number(formData.delaisConsultation || 10));
    if (formData.dateDebutOperation !== debutStr) {
      setFormData({ ...formData, dateDebutOperation: debutStr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.delaisConsultation, viewMode, setFormData]);

  // Recalcule la date de fin d'operation depuis debut + duree previsionnelle.
  useEffect(() => {
    if (!setFormData || viewMode || !formData.dateDebutOperation) return;
    const finStr = computeDateFinOperation(formData.dateDebutOperation, Number(formData.delaisFinOperation || 10));
    if (formData.dateFinOperation !== finStr) {
      setFormData({ ...formData, dateFinOperation: finStr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dateDebutOperation, formData.delaisFinOperation, viewMode, setFormData]);

  const dateFr = (value?: string): string => (value ? new Date(value).toLocaleDateString('fr-FR') : '');

  // Erreur affichee : si le champ a ete touche (blur), on calcule le format en direct sur la valeur
  // courante ; sinon on retombe sur l'erreur remontee par la validation de sauvegarde / le 422.
  const mailError = touched.moeMail
    ? (formData.moeMail?.trim() && !isValidEmail(formData.moeMail) ? FORMAT_ERROR_MS.moeMail : '')
    : (errors.moeMail || '');
  const telError = touched.moeTelephone
    ? (formData.moeTelephone?.trim() && !isValidPhoneFr(formData.moeTelephone) ? FORMAT_ERROR_MS.moeTelephone : '')
    : (errors.moeTelephone || '');

  return (
    <div className={`opms__admin-grid${viewMode ? ' view-mode' : ''}`}>
      {/* Ligne 1 : Nom de l'ouvrage | Nom de l'operation | Reference interne | Type de commande
          (4 quadrants span-3, meme rythme que les lignes localisation / MOE / dates ci-dessous). */}
      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Nom de l'ouvrage {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{formData.nomOuvrage || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.nomOuvrage || ''}
              onChange={(e) => update({ nomOuvrage: e.target.value })}
              className={errors.nomOuvrage ? 'error' : ''}
              placeholder="ex : Centre Hospitalier Sainte Justine"
            />
            <span className="opms__char-count">{(formData.nomOuvrage || '').length}/{CHAMP_PHRASE_MAX}</span>
            {errors.nomOuvrage && <span className="error-message">{errors.nomOuvrage}</span>}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Nom de l'opération {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{formData.nomOperation || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.nomOperation || ''}
              onChange={(e) => update({ nomOperation: e.target.value })}
              className={errors.nomOperation ? 'error' : ''}
              placeholder="ex : Réhabilitation de la partie sud est"
            />
            <span className="opms__char-count">{(formData.nomOperation || '').length}/{CHAMP_PHRASE_MAX}</span>
            {errors.nomOperation && <span className="error-message">{errors.nomOperation}</span>}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>N° Référence interne</label>
        {viewMode ? (
          <div className="input-inactif">{formData.referenceInterne || '-'}</div>
        ) : (
          <input
            type="text"
            maxLength={100}
            value={formData.referenceInterne || ''}
            onChange={(e) => update({ referenceInterne: e.target.value })}
            placeholder="ex : 23456789009876"
          />
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        {/* Type de commande : derive (Initiale / Complementaire), lecture seule. */}
        <label>Type de commande</label>
        <div className="input-inactif">{formData.typeCommande || 'Initiale'}</div>
      </div>

      {/* Ligne 2 : Adresse (une seule ligne) */}
      {/* Adresse sur UNE ligne, pleine largeur : code postal, ville et pays ne sont plus des champs
          a part, ils sont derives de la suggestion IGN choisie (cf. handleSelectAdresse). Le back
          continue de recevoir les trois separement — seul l'affichage change.
          En LECTURE SEULE ils reapparaissent, composes dans la meme ligne : sans eux on ne lirait que
          la voie, et l'operation perdrait sa localisation a l'ecran comme a l'impression. */}
      <div className="form-group" style={{ gridColumn: 'span 12' }}>
        <label>Adresse de l'opération {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{adresseComplete || '-'}</div>
        ) : (
          <div className="autocomplete-container">
            <input
              type="text"
              id="adresse-autocomplete"
              value={adresseInput}
              onChange={handleAdresseInputChange}
              className={errors.adresse ? 'error' : ''}
              placeholder="ex : 43 Chemin des étoiles"
              autoComplete="off"
            />
            {adresseSuggestions.length > 0 && (
              <ul className="autocomplete-list" role="listbox">
                {adresseSuggestions.map((suggestion, index) => (
                  <li key={index} role="option" aria-selected={false} onClick={() => handleSelectAdresse(suggestion)}>
                    {suggestion.label}
                  </li>
                ))}
              </ul>
            )}
            {errors.adresse && <span className="error-message">{errors.adresse}</span>}
          </div>
        )}
      </div>

      {/* Code postal, ville et pays : DERIVES de la suggestion IGN, plus affiches. Ils restent
          dans le formulaire et partent en base comme avant — seul le rendu change, l'adresse
          tenant desormais sur une seule ligne. Les masquer impose de valider l'adresse par une
          suggestion : une saisie libre les vide (cf. handleAdresseInputChange), et l'erreur
          remonte sur le champ Adresse, le seul visible. */}

      {/* Ligne 3 : Cabinet de maitrise d'oeuvre | Interlocuteur | Mail | Telephone */}
      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Cabinet de maitrise d'œuvre éventuel ?</label>
        {viewMode ? (
          <div className="input-inactif">{formData.coordonneesMaitrise || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.coordonneesMaitrise || ''}
              onChange={(e) => update({ coordonneesMaitrise: e.target.value })}
              placeholder="ex : Cabinet OREO"
            />
            <span className="opms__char-count">{(formData.coordonneesMaitrise || '').length}/{CHAMP_PHRASE_MAX}</span>
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Interlocuteur</label>
        {viewMode ? (
          <div className="input-inactif">{formData.moeInterlocuteur || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.moeInterlocuteur || ''}
              onChange={(e) => update({ moeInterlocuteur: e.target.value })}
              placeholder="ex : John SMITH"
            />
            <span className="opms__char-count">{(formData.moeInterlocuteur || '').length}/{CHAMP_PHRASE_MAX}</span>
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Mail</label>
        {viewMode ? (
          <div className="input-inactif">{formData.moeMail || '-'}</div>
        ) : (
          <>
            <input
              type="email"
              maxLength={255}
              value={formData.moeMail || ''}
              onChange={(e) => update({ moeMail: e.target.value })}
              onBlur={() => setTouched((t) => ({ ...t, moeMail: true }))}
              className={mailError ? 'error' : ''}
              placeholder="ex : john.smith@gmail.com"
            />
            {mailError && <span className="error-message">{mailError}</span>}
          </>
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Téléphone</label>
        {viewMode ? (
          <div className="input-inactif">{formData.moeTelephone || '-'}</div>
        ) : (
          <>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={formData.moeTelephone || ''}
              onChange={(e) => update({ moeTelephone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              onBlur={() => setTouched((t) => ({ ...t, moeTelephone: true }))}
              className={telError ? 'error' : ''}
              placeholder="ex : 0678901234"
            />
            {telError && <span className="error-message">{telError}</span>}
          </>
        )}
      </div>

      {/* Ligne 4 : Date demarrage | Duree previsionnelle | Date fin (calculee) | Phases */}
      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Date prévisionnelle de démarrage {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{dateFr(formData.dateDebutOperation)}</div>
        ) : (
          <input
            type="date"
            min={finConsultationStr}
            value={formData.dateDebutOperation || ''}
            onChange={(e) => {
              hasManuallyEditedDateRef.current = true;
              update({ dateDebutOperation: e.target.value });
            }}
            className="date-input"
          />
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Durée prévisionnelle (jours calendaires) {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
        {viewMode ? (
          <div className="input-inactif">{`${formData.delaisFinOperation || 10} jours`}</div>
        ) : (
          <input
            type="number"
            min={10}
            value={formData.delaisFinOperation || 10}
            onChange={(e) => update({ delaisFinOperation: Math.max(10, Number(e.target.value)) })}
            placeholder="ex : 456"
          />
        )}
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Date prévisionnelle de fin d'opération</label>
        <div className="input-inactif">{dateFr(formData.dateFinOperation)}</div>
      </div>

      <div className="form-group" style={{ gridColumn: 'span 3' }}>
        <label>Phases ?</label>
        {viewMode ? (
          <div className="input-inactif">{formData.phases || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={CHAMP_PHRASE_MAX}
              value={formData.phases || ''}
              onChange={(e) => update({ phases: e.target.value })}
              placeholder="ex : 4 phases opérationnelles"
            />
            <span className="opms__char-count">{(formData.phases || '').length}/{CHAMP_PHRASE_MAX}</span>
          </>
        )}
      </div>
    </div>
  );
};

export default OperationFormulaireAdmin;
